import React, { useMemo, useRef, useState } from "react";
import ReactQuill from "react-quill";
import Calendar from "react-calendar";
import TimePicker from "react-time-picker";
import Clock from "react-clock";
import "react-quill/dist/quill.snow.css";
import "react-calendar/dist/Calendar.css";
import "react-time-picker/dist/TimePicker.css";
import "react-clock/dist/Clock.css";
import { buildAdminNewsSlug, fileToBase64 } from "./api";
import { FISP_TV_ADMIN_CATEGORY_OPTIONS } from "./config";

const QUILL_MODULES = {
    toolbar: [
        [{ header: [1, 2, 3, 4, 5, 6, false] }],
        [{ font: [] }],
        [{ list: "ordered" }, { list: "bullet" }],
        ["bold", "italic", "underline"],
        [{ color: [] }, { background: [] }],
        [{ align: [] }],
        ["image", "blockquote", "code-block"],
        ["clean"],
        ["link"],
    ],
};

const SUBMIT_COOLDOWN_MS = 5000;

function parseDraftDate(value) {
    if (!value) {
        return null;
    }

    const [year, month, day] = String(value).split("-").map(Number);

    if (!year || !month || !day) {
        return null;
    }

    return new Date(year, month - 1, day);
}

function formatDraftDate(dateValue) {
    if (!(dateValue instanceof Date) || Number.isNaN(dateValue.getTime())) {
        return "";
    }

    const year = dateValue.getFullYear();
    const month = String(dateValue.getMonth() + 1).padStart(2, "0");
    const day = String(dateValue.getDate()).padStart(2, "0");

    return `${year}-${month}-${day}`;
}

function formatScheduleSummary(dateValue, timeValue) {
    const parsedDate = parseDraftDate(dateValue);

    if (!parsedDate) {
        return "Elegí una fecha y una hora de publicación";
    }

    const formattedDate = new Intl.DateTimeFormat("es-AR", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
    }).format(parsedDate);

    return `${formattedDate} a las ${timeValue || "00:00"} hs`;
}

export default function NoteForm({
    draft,
    setDraft,
    allNotes = [],
    onSubmit,
    submitLabel,
    submitBusyLabel,
    submitting = false,
    disabled = false,
    helperMessage = "",
    showSlugField = true,
    showImageUrlField = true,
    showRelatedFields = true,
    showPublishSummary = true,
    allowImageUploadOnly = false,
    feedback = null,
}) {
    const [imageBusy, setImageBusy] = useState(false);
    const [imageError, setImageError] = useState("");
    const [coverImageBusy, setCoverImageBusy] = useState(false);
    const [coverImageError, setCoverImageError] = useState("");
    const [cooldown, setCooldown] = useState(false);
    const cooldownTimer = useRef(null);

    const titleCharCount = draft.title.trim().length;
    const titleTooShort = titleCharCount > 0 && titleCharCount < 10;

    const selectedCalendarDate = useMemo(() => parseDraftDate(draft.publishDate), [draft.publishDate]);

    const relatedOptions = allNotes
        .filter((item) => item?.titulo && item?.url && item.url !== draft.slug)
        .map((item) => ({
            value: item.url,
            label: item.titulo,
        }));

    const updateDraft = (key, value) => {
        setDraft((currentDraft) => ({ ...currentDraft, [key]: value }));
    };

    const handleTitleChange = (event) => {
        const nextTitle = event.target.value;

        setDraft((currentDraft) => ({
            ...currentDraft,
            title: nextTitle,
            slug:
                currentDraft.slug === buildAdminNewsSlug(currentDraft.title) || !currentDraft.slug
                    ? buildAdminNewsSlug(nextTitle)
                    : currentDraft.slug,
        }));
    };

    const handleImageFile = async (event) => {
        const file = event.target.files?.[0];

        if (!file) {
            return;
        }

        if (file.size > 250 * 1024) {
            setImageError("La imagen no puede superar los 250 KB.");
            event.target.value = "";
            return;
        }

        setImageError("");
        setImageBusy(true);

        try {
            const base64 = await fileToBase64(file);
            updateDraft("image", String(base64 || ""));
        } finally {
            setImageBusy(false);
        }
    };

    const handleCoverImageFile = async (event) => {
        const file = event.target.files?.[0];

        if (!file) {
            return;
        }

        if (file.size > 500 * 1024) {
            setCoverImageError("La imagen no puede superar los 500 KB.");
            event.target.value = "";
            return;
        }

        setCoverImageError("");
        setCoverImageBusy(true);

        try {
            const base64 = await fileToBase64(file);
            updateDraft("coverImage", String(base64 || ""));
        } finally {
            setCoverImageBusy(false);
        }
    };

    const handleSubmit = (event) => {
        event.preventDefault();

        if (titleTooShort || cooldown || submitting) {
            return;
        }

        setCooldown(true);
        clearTimeout(cooldownTimer.current);
        cooldownTimer.current = setTimeout(() => setCooldown(false), SUBMIT_COOLDOWN_MS);

        onSubmit(event);
    };

    const isSubmitDisabled = disabled || submitting || imageBusy || coverImageBusy || cooldown || titleTooShort;

    return (
        <form className="tv-admin-form" onSubmit={handleSubmit}>
            <div className="tv-admin-form-grid">
                <div className="tv-admin-field tv-admin-field-full">
                    <label htmlFor="tv-admin-title">Título</label>
                    <input
                        id="tv-admin-title"
                        type="text"
                        value={draft.title}
                        onChange={handleTitleChange}
                        minLength={10}
                        maxLength={100}
                        disabled={disabled || submitting}
                        required
                        style={titleTooShort ? { borderColor: "#d73f2f", boxShadow: "0 0 0 2px rgba(215,63,47,0.18)" } : undefined}
                    />
                    <div className="tv-admin-field-meta" style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                        {titleTooShort ? (
                            <span style={{ color: "#d73f2f", fontSize: "inherit" }}>
                                El título debe tener al menos 10 caracteres ({titleCharCount}/10)
                            </span>
                        ) : (
                            <span></span>
                        )}
                        <span>{draft.title.length}/100</span>
                    </div>
                </div>

                {showSlugField ? (
                    <div className="tv-admin-field">
                        <label htmlFor="tv-admin-slug">URL de la nota</label>
                        <input
                            id="tv-admin-slug"
                            type="text"
                            value={draft.slug}
                            onChange={(event) => updateDraft("slug", event.target.value)}
                            disabled={disabled || submitting}
                            required
                        />
                    </div>
                ) : null}

                <div className="tv-admin-field">
                    <label htmlFor="tv-admin-category">Categoría</label>
                    <select
                        id="tv-admin-category"
                        value={draft.category}
                        onChange={(event) => updateDraft("category", event.target.value)}
                        disabled={disabled || submitting}
                        required
                    >
                        <option value="">Seleccionar categoría</option>
                        {FISP_TV_ADMIN_CATEGORY_OPTIONS.map((item) => (
                            <option key={item.value} value={item.value}>
                                {item.label}
                            </option>
                        ))}
                    </select>
                </div>

                <div className="tv-admin-field tv-admin-field-full">
                    <label htmlFor="tv-admin-description">Descripción</label>
                    <textarea
                        id="tv-admin-description"
                        rows="3"
                        value={draft.description}
                        onChange={(event) => updateDraft("description", event.target.value)}
                        disabled={disabled || submitting}
                        required
                    />
                </div>

                <div className="tv-admin-field tv-admin-field-full">
                    <label htmlFor="tv-admin-image-file">Imagen</label>

                    {showImageUrlField && !allowImageUploadOnly ? (
                        <input
                            id="tv-admin-image-url"
                            type="text"
                            value={draft.image}
                            onChange={(event) => updateDraft("image", event.target.value)}
                            disabled={disabled || submitting || imageBusy}
                            required
                        />
                    ) : null}

                    <div className="tv-admin-file-row">
                        <input
                            id="tv-admin-image-file"
                            className="tv-admin-file-input"
                            type="file"
                            accept="image/png,image/jpeg,image/webp"
                            onChange={handleImageFile}
                            disabled={disabled || submitting}
                            required={allowImageUploadOnly && !draft.image}
                        />

                        <label htmlFor="tv-admin-image-file" className="tv-admin-file-trigger">
                            {imageBusy ? "Procesando archivo..." : "Seleccionar archivo"}
                        </label>

                        <span className={`tv-admin-file-name ${imageError ? "is-error" : ""}`}>
                            {imageError || (draft.image ? "Archivo listo" : "PNG, JPG o WEBP hasta 250 KB")}
                        </span>
                    </div>

                    {draft.image ? (
                        <div className="tv-admin-image-preview">
                            <img src={draft.image} alt={draft.title || "Vista previa de la nota"} />
                        </div>
                    ) : null}
                </div>

                <div className="tv-admin-field tv-admin-field-full">
                    <label htmlFor="tv-admin-cover-image-file">Imagen de portada (opcional)</label>
                    <p className="tv-admin-field-note">
                        Esta foto es la que va a aparecer si la nota se posiciona primera en el Home. Si se sabe que la nota no va a ocupar ese lugar en ningún momento, mejor no subir nada para no hacer más lenta la carga de la nota. Esta imagen también se puede agregar desde el editor.
                    </p>

                    <div className="tv-admin-file-row">
                        <input
                            id="tv-admin-cover-image-file"
                            className="tv-admin-file-input"
                            type="file"
                            accept="image/png,image/jpeg,image/webp"
                            onChange={handleCoverImageFile}
                            disabled={disabled || submitting}
                        />

                        <label htmlFor="tv-admin-cover-image-file" className="tv-admin-file-trigger">
                            {coverImageBusy ? "Procesando archivo..." : "Seleccionar archivo"}
                        </label>

                        <span className={`tv-admin-file-name ${coverImageError ? "is-error" : ""}`}>
                            {coverImageError || (draft.coverImage ? "Archivo listo" : "PNG, JPG o WEBP hasta 500 KB")}
                        </span>
                    </div>

                    {draft.coverImage ? (
                        <div className="tv-admin-image-preview">
                            <img src={draft.coverImage} alt={draft.title || "Vista previa de portada"} />
                        </div>
                    ) : null}
                </div>

                <div className="tv-admin-field tv-admin-field-full">
                    <label htmlFor="tv-admin-video">Link o ID de YouTube</label>
                    <input
                        id="tv-admin-video"
                        type="text"
                        value={draft.videoUrl}
                        onChange={(event) => updateDraft("videoUrl", event.target.value)}
                        placeholder="Link completo o ID del video (opcional)"
                        disabled={disabled || submitting}
                    />
                </div>

                <div className="tv-admin-field tv-admin-field-full">
                    <label htmlFor="tv-admin-body">Cuerpo de la nota</label>
                    <div className="tv-admin-quill">
                        <style>{`.tv-admin-quill .ql-editor { padding: 1.5rem; }`}</style>
                        <ReactQuill
                            id="tv-admin-body"
                            theme="snow"
                            value={draft.body}
                            onChange={(nextValue) => updateDraft("body", nextValue)}
                            modules={QUILL_MODULES}
                            readOnly={disabled || submitting}
                            placeholder=""
                        />
                    </div>
                </div>

                <div className="tv-admin-field tv-admin-field-full">
                    <div className="tv-admin-schedule-panel">
                        <div className="tv-admin-schedule-calendar">
                            <span className="tv-admin-schedule-label">Fecha de publicación</span>
                            <Calendar
                                onChange={(nextValue) => updateDraft("publishDate", formatDraftDate(nextValue))}
                                value={selectedCalendarDate}
                                locale="es-AR"
                                minDetail="month"
                                next2Label={null}
                                prev2Label={null}
                            />
                        </div>

                        <div className="tv-admin-schedule-time">
                            <div className="tv-admin-time-picker-shell">
                                <div className="tv-admin-time-picker-control">
                                    <TimePicker
                                        onChange={(nextValue) => updateDraft("publishTime", nextValue || "00:00")}
                                        value={draft.publishTime || "00:00"}
                                        format="HH:mm"
                                        disableClock={true}
                                        clearIcon={null}
                                        disabled={disabled || submitting}
                                    />
                                </div>

                                <div className="tv-admin-time-clock-wrap">
                                    <Clock value={draft.publishTime || "00:00"} renderNumbers={true} />
                                </div>
                            </div>
                        </div>

                        <div className="tv-admin-schedule-message">
                            <span className="tv-admin-schedule-label">Se publicará</span>
                            <p>{formatScheduleSummary(draft.publishDate, draft.publishTime)}</p>
                        </div>
                    </div>
                </div>

                {showPublishSummary ? (
                    <div className="tv-admin-publish-box tv-admin-field-full">
                        <span>Resumen de publicación</span>
                        <strong>{draft.category || "Sin categoría"}</strong>
                        <p>
                            {draft.publishDate
                                ? `${draft.publishDate.split("-").reverse().join("/")} · ${draft.publishTime || "00:00"} hs`
                                : "Definí una fecha y hora de publicación"}
                        </p>
                    </div>
                ) : null}

                {showRelatedFields ? (
                    <>
                        <div className="tv-admin-field">
                            <label htmlFor="tv-admin-related-1">Relacionada 1</label>
                            <select
                                id="tv-admin-related-1"
                                value={draft.related1}
                                onChange={(event) => updateDraft("related1", event.target.value)}
                                disabled={disabled || submitting}
                            >
                                <option value="">Sin relación</option>
                                {relatedOptions.map((item) => (
                                    <option key={item.value} value={item.value}>
                                        {item.label}
                                    </option>
                                ))}
                            </select>
                        </div>

                        <div className="tv-admin-field">
                            <label htmlFor="tv-admin-related-2">Relacionada 2</label>
                            <select
                                id="tv-admin-related-2"
                                value={draft.related2}
                                onChange={(event) => updateDraft("related2", event.target.value)}
                                disabled={disabled || submitting}
                            >
                                <option value="">Sin relación</option>
                                {relatedOptions.map((item) => (
                                    <option key={item.value} value={item.value}>
                                        {item.label}
                                    </option>
                                ))}
                            </select>
                        </div>

                        <div className="tv-admin-field">
                            <label htmlFor="tv-admin-related-3">Relacionada 3</label>
                            <select
                                id="tv-admin-related-3"
                                value={draft.related3}
                                onChange={(event) => updateDraft("related3", event.target.value)}
                                disabled={disabled || submitting}
                            >
                                <option value="">Sin relación</option>
                                {relatedOptions.map((item) => (
                                    <option key={item.value} value={item.value}>
                                        {item.label}
                                    </option>
                                ))}
                            </select>
                        </div>
                    </>
                ) : null}
            </div>

            {helperMessage ? <p className="tv-admin-form-helper">{helperMessage}</p> : null}

            <div className="tv-admin-form-actions">
                {feedback ? (
                    <div className={`tv-admin-feedback tv-admin-form-actions-feedback is-${feedback.type}`}>
                        {feedback.message}
                    </div>
                ) : null}
                <button type="submit" className="tv-admin-primary-btn" disabled={isSubmitDisabled}>
                    {submitting ? submitBusyLabel : cooldown ? "Esperar un momento..." : submitLabel}
                </button>
            </div>
        </form>
    );
}
